import { Button } from '@/components/ui/button';
import { useUscgMapContext } from './UscgMapContext';
import type { InternalLayer } from 'hooks/internalLayer/types';
import { useCreateDraftInternalLayerVersionMutation } from 'hooks/internalLayerVersion/useCreateDraftInternalLayerVersionMutation';
import { LayerFeaturesContent } from './LayerFeaturesContent';
import { useDeleteDraftInternalLayerVersionMutation } from 'hooks/internalLayerVersion/useDeleteDraftInternalLayerVersionMutation';
import { useDeleteLocation } from 'hooks/icsLocation/useDeleteLocation';
import { fetchInternalLayerVersions } from 'hooks/internalLayerVersion/useListInternalLayerVersionsQuery';
import { InternalLayerVersionBanner } from './InternalLayerVersionBanner';
import { LayerWorkspaceBadge } from './LayerWorkspaceBadge';

export function CreateEditInternalLayerVersionContent({
  internalLayer,
  showCreateDraftButton,
  ...props
}: {
  internalLayer: InternalLayer;
  onBack: () => void;
  showCreateDraftButton?: boolean;
}) {
  const {
    stopDrawing,
    getSelectedVersionForLayer,
    setSelectedInternalLayerVersion,
    sidebarSelectedInternalLayer,
  } = useUscgMapContext();

  const createDraft = useCreateDraftInternalLayerVersionMutation();
  const deleteDraft = useDeleteDraftInternalLayerVersionMutation();
  const deleteLocation = useDeleteLocation();

  const selectedInternalLayerVersion = sidebarSelectedInternalLayer?.id
    ? getSelectedVersionForLayer(sidebarSelectedInternalLayer.id)
    : undefined;

  const onBack = () => {
    stopDrawing();
    props.onBack();
  };

  const onCreate = async () => {
    await createDraft.mutateAsync({
      internalLayerId: internalLayer.id,
    });
    onBack();
  };

  const onDelete = async () => {
    if (!selectedInternalLayerVersion?.id) {
      return;
    }

    await deleteDraft.mutateAsync({
      internalLayerVersionId: selectedInternalLayerVersion.id,
    });
    onBack();
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <h2 className="tw:mb-0 tw:flex tw:items-center tw:gap-2 tw:text-left tw:text-sm tw:font-semibold">
        <span className="tw:min-w-0 tw:truncate">{internalLayer.name}</span>
        <LayerWorkspaceBadge
          incidentId={internalLayer.incidentId}
          incidentName={internalLayer.incident?.name}
        />
      </h2>
      <InternalLayerVersionBanner layer={internalLayer} />
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        {showCreateDraftButton ? (
          <Button
            type="button"
            disabled={createDraft.isPending}
            onClick={() => void onCreate()}
          >
            {createDraft.isPending ? 'Creating…' : 'Create draft'}
          </Button>
        ) : (
          <Button
            type="button"
            variant="destructive"
            disabled={
              deleteDraft.isPending || !selectedInternalLayerVersion?.id
            }
            onClick={() => void onDelete()}
          >
            {deleteDraft.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        )}
        <Button type="button" variant="outline" onClick={onBack}>
          Cancel
        </Button>
      </div>
      <LayerFeaturesContent
        layer={internalLayer}
        layerType="internal"
        showAddObjectButton
        onDelete={
          showCreateDraftButton
            ? undefined
            : async (objectId) => {
                const response = await deleteLocation.mutateAsync(objectId);
                const versionId = response.data.internalLayerVersionId;
                if (!versionId) return;
                const versions = await fetchInternalLayerVersions({
                  filter: { internalLayerId: internalLayer.id },
                });
                const match = versions.data.find(
                  (version) => version.id === versionId
                );
                if (match) {
                  setSelectedInternalLayerVersion(internalLayer.id, match);
                }
              }
        }
      />
    </div>
  );
}
